import { Platform } from 'react-native'

/**
 * How long an iOS `<Modal>` fade-out takes to finish, with margin (UIKit cross-dissolve ≈ 300 ms).
 *
 * iOS presents every RN Modal from the root view controller. Asking it to present a new one
 * while another is still animating away is refused by UIKit, but RN has already marked the new
 * modal as presented: it never appears, and the half-finished transition can leave an invisible
 * layer that swallows every touch (specs/profile-page, Story 8).
 */
export const IOS_MODAL_DISMISS_SETTLE_MS = 450

/**
 * Upper bound for waiting on a Modal's own `onDismiss` before acting anyway, so an action is
 * never lost if the event does not arrive (e.g. the Modal unmounted while closing).
 */
export const IOS_MODAL_DISMISS_FALLBACK_MS = 1000

/** When a shared modal last started presenting or dismissing (ms since epoch); 0 = never. */
let lastNativeModalTransitionAt = 0

/** Records that a shared modal just started presenting or dismissing (specs/coin-compliance Story 5). */
export function markNativeModalTransition(now: number = Date.now()): void {
  lastNativeModalTransitionAt = now
}

/** How long until the last shared-modal transition has settled and another Modal may be presented. */
export function msUntilNativeModalTransitionsSettle(now: number = Date.now()): number {
  if (lastNativeModalTransitionAt === 0) return 0
  return Math.max(0, lastNativeModalTransitionAt + IOS_MODAL_DISMISS_SETTLE_MS - now)
}

/** Test helper — forget the last recorded transition. */
export function resetNativeModalTransitionsForTests(): void {
  lastNativeModalTransitionAt = 0
}

/**
 * Runs `action` once a native Modal that was just asked to close has finished closing.
 * iOS only: Android and web present modals independently, so the action runs immediately.
 */
export function runAfterNativeModalDismiss(action: () => void): void {
  if (Platform.OS !== 'ios') {
    action()
    return
  }
  setTimeout(action, IOS_MODAL_DISMISS_SETTLE_MS)
}
